import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslate } from "ra-core";
import {
  Bot,
  PhoneIncoming,
  PhoneMissed,
  PhoneOutgoing,
  Play,
  Voicemail,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { AsideSection } from "../atomic-crm/misc/AsideSection";
import {
  fetchCalls,
  formatDuration,
  formatPhone,
  phoneEnabled,
  recordingUrl,
  type CallRecord,
} from "./phoneApi";

interface CallsListProps {
  contactId?: number;
  companyId?: number;
}

/** Call history for a contact or company: outcome, duration, summary, recording and transcript. */
export function CallsList({ contactId, companyId }: CallsListProps) {
  const translate = useTranslate();
  const { data: calls = [], isLoading } = useQuery({
    queryKey: ["phone.calls", contactId ?? null, companyId ?? null],
    queryFn: () => fetchCalls({ contact_id: contactId, company_id: companyId }),
    enabled: phoneEnabled && (contactId != null || companyId != null),
    refetchInterval: 30_000,
  });
  if (!phoneEnabled) return null;

  return (
    <AsideSection title={translate("crm.phone.calls", { _: "Calls" })}>
      {isLoading && <span className="text-muted-foreground">…</span>}
      {!isLoading && calls.length === 0 && (
        <span className="text-muted-foreground">
          {translate("crm.phone.no_calls", { _: "No calls yet" })}
        </span>
      )}
      {calls.map((c) => (
        <CallRow key={c.id} call={c} />
      ))}
    </AsideSection>
  );
}

function CallRow({ call }: { call: CallRecord }) {
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [showTranscript, setShowTranscript] = useState(false);
  const Icon = iconFor(call);
  const other =
    call.direction === "inbound" ? call.from_number : call.to_number;
  const when = call.started_at ? new Date(call.started_at) : null;

  const play = async () => {
    if (!call.recording_path) return;
    setAudioUrl(await recordingUrl(call.recording_path));
  };

  return (
    <div className="border-b border-border/60 py-2 last:border-0">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Icon className="size-3.5" />
        <span>
          {when
            ? `${when.toLocaleDateString()} ${when.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
            : ""}
        </span>
        <span>· {formatDuration(call.duration_s)}</span>
        <span>· {formatPhone(other)}</span>
        {call.extension && <span>· ext {call.extension}</span>}
      </div>
      {call.summary && <p className="mt-1 text-sm">{call.summary}</p>}
      <div className="mt-1 flex flex-wrap items-center gap-2">
        {call.recording_path && !audioUrl && (
          <Button
            variant="outline"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={() => void play()}
          >
            <Play className="mr-1 size-3" /> Play
          </Button>
        )}
        {audioUrl && <audio controls src={audioUrl} className="h-8 w-full" />}
        {call.transcript && call.transcript.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={() => setShowTranscript((v) => !v)}
          >
            {showTranscript ? "Hide transcript" : "Transcript"}
          </Button>
        )}
      </div>
      {showTranscript && call.transcript && (
        <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
          {call.transcript.map((line, i) => (
            <li key={i}>
              {line.speaker !== "call" && (
                <span className="font-medium">{line.speaker}: </span>
              )}
              {line.text}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function iconFor(call: CallRecord) {
  if (call.handled_by === "ai") return Bot;
  if (call.handled_by === "voicemail") return Voicemail;
  if (call.handled_by === "missed") return PhoneMissed;
  return call.direction === "outbound" ? PhoneOutgoing : PhoneIncoming;
}
